import React, { useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  Divider,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../shared/firebase';

const PROVIDERS = [
  { key: 'deudafix', label: 'Deudafix' },
  { key: 'unifye', label: 'Unifye' },
];

const MAX_ROWS = 500;

// Header aliases so the operator can upload the sheet as they already keep it.
const FIELD_ALIASES = {
  nombre: ['nombre', 'nombres', 'name', 'first_name', 'firstname', 'primer titular'],
  apellidos: ['apellidos', 'apellido', 'last_name', 'lastname', 'surname'],
  telefono: ['telefono', 'teléfono', 'movil', 'móvil', 'mobile', 'phone', 'celular'],
  correo: ['correo', 'email', 'e-mail', 'mail'],
};

const norm = (s) =>
  String(s == null ? '' : s)
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .trim()
    .toLowerCase();

const digits = (v) => String(v == null ? '' : v).replace(/\D/g, '');

const mapHeaders = (headers) => {
  const map = {};
  headers.forEach((h, i) => {
    const n = norm(h);
    Object.entries(FIELD_ALIASES).forEach(([field, aliases]) => {
      if (map[field] === undefined && aliases.includes(n)) map[field] = i;
    });
  });
  return map;
};

const rowIssue = (row) => {
  if (!row.nombre) return 'falta nombre';
  if (!row.correo.includes('@')) return 'correo no válido';
  if (digits(row.telefono).length < 9) return 'teléfono no válido';
  return null;
};

const statusChip = (r) => {
  if (!r) return <Chip size="small" label="pendiente" variant="outlined" />;
  if (r.sending) return <Chip size="small" label="enviando…" color="info" variant="outlined" />;
  if (r.ok) return <Chip size="small" label="enviado" color="success" />;
  if (r.status === 'duplicado')
    return <Chip size="small" label="ya registrado" color="warning" />;
  return <Chip size="small" label={r.error || `error ${r.httpStatus || ''}`} color="error" />;
};

const ApiManualPage = () => {
  const [provider, setProvider] = useState('deudafix');
  const [form, setForm] = useState({ nombre: '', apellidos: '', telefono: '', correo: '' });
  const [single, setSingle] = useState(null);
  const [sendingSingle, setSendingSingle] = useState(false);

  // Petición de Diego (19/08): en el masivo, reintentar los "correo ya
  // registrado" añadiendo "+" al correo para que el caso entre igualmente.
  const [forceDup, setForceDup] = useState(true);
  const [rows, setRows] = useState([]);
  const [fileName, setFileName] = useState('');
  const [parseError, setParseError] = useState('');
  const [results, setResults] = useState({});
  const [bulkBusy, setBulkBusy] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const fileRef = useRef(null);

  const callSend = async (lead, batchId) => {
    const call = httpsCallable(functions, 'sendManualAffiliateLead');
    const res = await call({ provider, lead, batchId, forceDuplicate: forceDup });
    return res.data || {};
  };

  const sendSingle = async () => {
    setSendingSingle(true);
    setSingle(null);
    try {
      const data = await callSend({ ...form, telefono: digits(form.telefono) });
      setSingle(data);
    } catch (err) {
      setSingle({ ok: false, error: err?.message || 'Error al enviar' });
    } finally {
      setSendingSingle(false);
    }
  };

  const onFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setParseError('');
    setResults({});
    setFileName(file.name);
    try {
      // Loaded on demand: the sheet parser is heavy and only this page needs it.
      const { read, utils } = await import('xlsx');
      const buf = await file.arrayBuffer();
      const wb = read(buf, { type: 'array' });
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const matrix = utils.sheet_to_json(sheet, { header: 1, blankrows: false, raw: false });
      if (!matrix.length) throw new Error('El archivo está vacío.');

      const map = mapHeaders(matrix[0] || []);
      if (map.nombre === undefined || map.correo === undefined || map.telefono === undefined) {
        throw new Error(
          'No encuentro las columnas. La primera fila debe tener las cabeceras: Nombre, Apellidos (opcional), Telefono, Correo.'
        );
      }
      const parsed = matrix
        .slice(1)
        .map((r) => ({
          nombre: String(r[map.nombre] ?? '').trim(),
          apellidos: map.apellidos === undefined ? '' : String(r[map.apellidos] ?? '').trim(),
          telefono: digits(r[map.telefono]),
          correo: String(r[map.correo] ?? '').trim(),
        }))
        .filter((r) => r.nombre || r.correo || r.telefono);

      if (!parsed.length) throw new Error('No hay filas con datos.');
      if (parsed.length > MAX_ROWS) {
        throw new Error(`Máximo ${MAX_ROWS} filas por envío (el archivo trae ${parsed.length}).`);
      }
      setRows(parsed);
    } catch (err) {
      setRows([]);
      setParseError(err?.message || 'No he podido leer el archivo.');
    }
  };

  const valid = useMemo(() => rows.map((r) => !rowIssue(r)), [rows]);
  const sendableIdx = useMemo(
    () => rows.map((_, i) => i).filter((i) => valid[i] && !results[i]?.ok),
    [rows, valid, results]
  );

  const sendBulk = async () => {
    setBulkBusy(true);
    const batchId = `manual-${Date.now()}`;
    const queue = [...sendableIdx];
    setProgress({ done: 0, total: queue.length });
    let done = 0;

    // Three at a time: fast enough for a few hundred rows without hammering
    // the provider.
    const worker = async () => {
      while (queue.length) {
        const i = queue.shift();
        setResults((prev) => ({ ...prev, [i]: { sending: true } }));
        try {
          const data = await callSend(rows[i], batchId);
          setResults((prev) => ({ ...prev, [i]: data }));
        } catch (err) {
          setResults((prev) => ({ ...prev, [i]: { ok: false, error: err?.message || 'error' } }));
        }
        done += 1;
        setProgress((p) => ({ ...p, done }));
      }
    };
    await Promise.all([worker(), worker(), worker()]);
    setBulkBusy(false);
  };

  const sentOk = Object.values(results).filter((r) => r && r.ok).length;
  const failed = Object.values(results).filter((r) => r && !r.ok && !r.sending).length;

  // The rejected rows are the ones the operator has to work by hand, so let
  // them leave with the reason attached.
  const downloadFailed = () => {
    const lines = [['Nombre', 'Apellidos', 'Telefono', 'Correo', 'Motivo'].join(';')];
    rows.forEach((r, i) => {
      const res = results[i];
      const issue = rowIssue(r);
      if (!issue && (!res || res.ok || res.sending)) return;
      const motivo = issue
        ? `No enviada: ${issue}`
        : res.status === 'duplicado'
        ? 'Ya registrado en el proveedor'
        : res.error || res.message || `Error ${res.httpStatus || ''}`;
      lines.push(
        [r.nombre, r.apellidos, r.telefono, r.correo, String(motivo).replace(/[\r\n;]+/g, ' ')].join(';')
      );
    });
    const blob = new Blob(['﻿' + lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'no-enviados.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const pendingCount = rows.filter((r, i) => {
    const res = results[i];
    return rowIssue(r) || (res && !res.ok && !res.sending);
  }).length;

  return (
    <Box sx={{ maxWidth: 1100 }}>
      <Typography variant="h5" sx={{ mb: 1, fontWeight: 600 }}>
        API Manual
      </Typography>
      <Alert severity="warning" sx={{ mb: 2 }}>
        Los envíos de esta página son <strong>reales</strong>: el caso entra en el sistema del
        proveedor en el momento en que pulsas Enviar. Cada envío queda registrado con tu usuario.
      </Alert>

      <FormControlLabel
        sx={{ display: 'block', mb: 1 }}
        control={<Checkbox size="small" checked={forceDup} onChange={(e) => setForceDup(e.target.checked)} />}
        label={
          <Typography variant="body2" color="text.secondary">
            Si el proveedor dice que <strong>el correo ya está registrado</strong>, reintentar añadiendo «+» al
            correo para que el caso entre igualmente (crea un caso nuevo aunque el cliente ya estuviera fichado).
          </Typography>
        }
      />

      <FormControl size="small" sx={{ minWidth: 220, mb: 2 }}>
        <InputLabel id="prov-label">Proveedor</InputLabel>
        <Select
          labelId="prov-label"
          label="Proveedor"
          value={provider}
          onChange={(e) => setProvider(e.target.value)}
        >
          {PROVIDERS.map((p) => (
            <MenuItem key={p.key} value={p.key}>
              {p.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            Envío individual
          </Typography>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
            <TextField
              size="small"
              label="Nombre"
              required
              value={form.nombre}
              onChange={(e) => setForm({ ...form, nombre: e.target.value })}
              fullWidth
            />
            <TextField
              size="small"
              label="Apellidos"
              value={form.apellidos}
              onChange={(e) => setForm({ ...form, apellidos: e.target.value })}
              fullWidth
            />
            <TextField
              size="small"
              label="Teléfono"
              required
              value={form.telefono}
              onChange={(e) => setForm({ ...form, telefono: e.target.value })}
              fullWidth
            />
            <TextField
              size="small"
              label="Correo"
              required
              value={form.correo}
              onChange={(e) => setForm({ ...form, correo: e.target.value })}
              fullWidth
            />
          </Stack>
          <Stack direction="row" spacing={2} alignItems="center">
            <Button
              variant="contained"
              startIcon={sendingSingle ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
              disabled={sendingSingle || !!rowIssue({ ...form, telefono: digits(form.telefono) })}
              onClick={sendSingle}
            >
              Enviar
            </Button>
            {single && (
              <Alert severity={single.ok ? 'success' : 'error'} sx={{ py: 0 }}>
                {single.ok
                  ? `Enviado a ${provider}${single.emailAdjusted ? ` (correo ajustado: ${single.emailUsed})` : ''}`
                  : `No enviado: ${single.error || single.message || single.status} ${
                      single.httpStatus ? `(HTTP ${single.httpStatus})` : ''
                    }`}
              </Alert>
            )}
          </Stack>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            Cargar Excel / CSV
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            La primera fila debe ser la cabecera con: Nombre, Apellidos (opcional), Telefono,
            Correo. Máximo {MAX_ROWS} filas por envío.
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Button variant="outlined" startIcon={<UploadFileIcon />} onClick={() => fileRef.current?.click()}>
              Seleccionar archivo
            </Button>
            <input
              ref={fileRef}
              type="file"
              accept=".csv,.xlsx,.xls,.txt"
              onChange={onFile}
              style={{ display: 'none' }}
            />
            {fileName && (
              <Typography variant="body2" color="text.secondary">
                {fileName} — {rows.length} fila(s)
              </Typography>
            )}
          </Stack>

          {parseError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {parseError}
            </Alert>
          )}

          {rows.length > 0 && (
            <>
              <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
                <Button
                  variant="contained"
                  startIcon={bulkBusy ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
                  disabled={bulkBusy || sendableIdx.length === 0}
                  onClick={sendBulk}
                >
                  Enviar {sendableIdx.length} caso(s)
                </Button>
                {valid.filter(Boolean).length !== rows.length && (
                  <Typography variant="body2" color="warning.main">
                    {rows.length - valid.filter(Boolean).length} fila(s) no se enviarán por datos
                    incompletos.
                  </Typography>
                )}
                {(sentOk > 0 || failed > 0) && (
                  <Typography variant="body2" color="text.secondary">
                    {sentOk} enviado(s) · {failed} sin enviar
                  </Typography>
                )}
                {pendingCount > 0 && !bulkBusy && (
                  <Button size="small" variant="text" onClick={downloadFailed}>
                    Descargar los no enviados ({pendingCount})
                  </Button>
                )}
              </Stack>

              {bulkBusy && (
                <LinearProgress
                  variant="determinate"
                  value={progress.total ? (progress.done / progress.total) * 100 : 0}
                  sx={{ mb: 2 }}
                />
              )}

              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Previsualización
              </Typography>
              <TableContainer sx={{ maxHeight: 420 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>#</TableCell>
                      <TableCell>Nombre</TableCell>
                      <TableCell>Apellidos</TableCell>
                      <TableCell>Teléfono</TableCell>
                      <TableCell>Correo</TableCell>
                      <TableCell>Estado</TableCell>
                      <TableCell>Respuesta</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((r, i) => {
                      const issue = rowIssue(r);
                      return (
                        <TableRow key={i} sx={issue ? { opacity: 0.55 } : undefined}>
                          <TableCell>{i + 1}</TableCell>
                          <TableCell>{r.nombre}</TableCell>
                          <TableCell>{r.apellidos}</TableCell>
                          <TableCell>{r.telefono}</TableCell>
                          <TableCell>{r.correo}</TableCell>
                          <TableCell>
                            {issue ? (
                              <Chip size="small" label={`no se enviará · ${issue}`} variant="outlined" />
                            ) : (
                              statusChip(results[i])
                            )}
                          </TableCell>
                          <TableCell sx={{ maxWidth: 280, fontSize: 12, color: 'text.secondary' }}>
                            {results[i]?.message || results[i]?.error || ''}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default ApiManualPage;
